import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useCreateFlatStore } from "../hooks/useCreateFlatStore";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import {
  trpcReact,
  setTurnstileToken,
  clearTurnstileToken,
} from "../trpc_client";
import { useTurnstile } from "../hooks/useTurnstile";
import TurnstileWidget from "../components/TurnstileWidget";
import AuthModal from "../components/AuthModal";
import { usePostHog } from "posthog-js/react";

// Step components
import FlatNameStep from "../components/createFlat/FlatNameStep";
import MembersStep from "../components/createFlat/MembersStep";
import LocationStep from "../components/createFlat/LocationStep";
import AvailabilityStep from "../components/createFlat/AvailabilityStep";
import PricingStep from "../components/createFlat/PricingStep";
import RoomDetailsStep from "../components/createFlat/RoomDetailsStep";
import ImagesStep from "../components/createFlat/ImagesStep";
import DescriptionStep from "../components/createFlat/DescriptionStep";
import AttributesStep from "../components/createFlat/AttributesStep";
import QuestionsStep from "../components/createFlat/QuestionsStep";
import SuccessStep from "../components/createFlat/SuccessStep";

const NUMBER_OF_STEPS = 10;

interface CreatedFlatData {
  flat_id: number;
  room_id: number;
  join_code: string;
}

const CreateFlat: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useCreateFlatStore();
  const { user, resendVerificationEmail } = useAuth();
  const posthog = usePostHog();
  const {
    isProfileComplete,
    needsOnboarding,
    isLoading: profileLoading,
  } = useProfile();

  const fromSignup = (location.state as any)?.fromSignup === true;
  const {
    containerNeutralSolidSurface,
    containerNeutralLightMedium,
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
    textNeutralOnPrimary,
  } = useColors();

  const {
    hasError: turnstileError,
    resetToken,
    siteKey,
    widgetRef,
    onExpire,
    onError,
  } = useTurnstile();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [verifyEmailSent, setVerifyEmailSent] = useState(false);
  const [verifyEmailError, setVerifyEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdFlat, setCreatedFlat] = useState<CreatedFlatData | null>(null);

  useEffect(() => {
    posthog?.capture("flat_creation_started", { surface: "create_flat_page" });
  }, [posthog]);

  useEffect(() => {
    if (fromSignup) return;

    if (user && !profileLoading && !isProfileComplete) {
      if (needsOnboarding) {
        posthog?.capture("onboarding_gate_redirected", {
          surface: "create_flat_page",
          destination: "signup",
          is_authenticated: true,
        });
        navigate("/signup", { replace: true });
      } else {
        posthog?.capture("onboarding_gate_redirected", {
          surface: "create_flat_page",
          destination: "profile_edit",
          is_authenticated: true,
        });
        navigate("/profile/edit", { replace: true });
      }
    }
  }, [
    user,
    profileLoading,
    isProfileComplete,
    needsOnboarding,
    navigate,
    fromSignup,
    posthog,
  ]);

  const createFlatMutation = trpcReact.createFlat.useMutation();

  useEffect(() => {
    document.body.style.backgroundColor = containerNeutralSolidSurface;
    document.documentElement.style.backgroundColor =
      containerNeutralSolidSurface;
    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [containerNeutralSolidSurface]);

  const handleClose = () => {
    if (createdFlat) {
      posthog?.capture("flat_creation_flow_closed", {
        completed: true,
        final_step: store.step,
      });
      store.reset();
      navigate(`/app/flat-view/${createdFlat.flat_id}`, { replace: true });
      return;
    }

    posthog?.capture("flat_creation_flow_closed", {
      completed: false,
      abandoned_at_step: store.step,
    });
    navigate("/flats");
  };

  const handleNext = () => {
    if (store.step < NUMBER_OF_STEPS - 1) {
      posthog?.capture("flat_creation_step_completed", {
        step: store.step,
        total_steps: NUMBER_OF_STEPS,
      });
      store.nextStep();
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    posthog?.capture("flat_creation_step_back", {
      from_step: store.step,
      to_step: store.step - 1,
    });
    store.prevStep();
  };

  const handleResendVerification = async () => {
    setVerifyEmailError(null);
    try {
      await resendVerificationEmail();
      setVerifyEmailSent(true);
    } catch (error: any) {
      if (error?.code === "auth/too-many-requests") {
        setVerifyEmailError(
          t("verify_email_too_many") ||
            "Too many requests. Please try again later.",
        );
      } else {
        setVerifyEmailError(
          t("verify_email_error") ||
            "Failed to send verification email. Please try again.",
        );
      }
    }
  };

  const handleRefreshVerification = async () => {
    if (!user) return;
    await user.reload();
    if (user.emailVerified) {
      setShowVerifyEmailModal(false);
      handleSubmit();
    } else {
      setVerifyEmailError(
        t("verify_email_not_yet") ||
          "Email not verified yet. Please check your inbox.",
      );
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      posthog?.capture("flat_creation_submit_blocked", {
        reason: "not_authenticated",
      });
      posthog?.capture("create_flat_submit_blocked", {
        surface: "create_flat_page",
        reason: "not_authenticated",
        is_authenticated: false,
      });
      setShowAuthModal(true);
      return;
    }

    if (!user.emailVerified) {
      posthog?.capture("flat_creation_submit_blocked", {
        reason: "email_not_verified",
      });
      posthog?.capture("create_flat_submit_blocked", {
        surface: "create_flat_page",
        reason: "email_not_verified",
        is_authenticated: true,
      });
      setShowVerifyEmailModal(true);
      setVerifyEmailSent(false);
      setVerifyEmailError(null);
      return;
    }

    if (store.images.some((img) => img.progress < 1)) {
      posthog?.capture("flat_creation_submit_blocked", {
        reason: "upload_in_progress",
      });
      posthog?.capture("create_flat_submit_blocked", {
        surface: "create_flat_page",
        reason: "upload_in_progress",
        is_authenticated: true,
      });
      setSubmitError(
        t("wait_for_uploads") || "Please wait for all images to upload",
      );
      return;
    }

    setSubmitError(null);
    posthog?.capture("turnstile_modal_shown", {
      surface: "create_flat_page",
      step: store.step,
      is_authenticated: true,
    });
    resetToken();
    setShowTurnstileModal(true);
  };

  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstileModal(false);
    posthog?.capture("turnstile_success", {
      surface: "create_flat_page",
      step: store.step,
      is_authenticated: !!user,
    });
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const fromDateIso =
        store.fromDate?.toISOString() || new Date().toISOString();
      const toDateIso = store.isPermanent
        ? fromDateIso
        : store.toDate?.toISOString() || fromDateIso;

      setTurnstileToken(token);
      posthog?.capture("flat_creation_submit_started", {
        has_flat_video: false,
        image_count: store.images.filter((img) => img.object_name).length,
        is_permanent: store.isPermanent,
        is_subsidised: store.isSubsidised,
        attribute_count: store.flatAttributes.length,
      });
      posthog?.capture("create_flat_submit_started", {
        surface: "create_flat_page",
        step: store.step,
        is_authenticated: !!user,
      });
      const result = await createFlatMutation.mutateAsync({
        flat_name: store.flatName.trim(),
        member_count: Math.max(2, parseInt(store.memberCount) || 0),
        flat_text: store.description.trim(),
        looking_for: store.lookingFor,
        looking_for_age_range: store.lookingForAgeRange,
        flat_location: store.location,
        latitude: parseFloat(store.latitude),
        longitude: parseFloat(store.longitude),
        flat_size: store.addingFlatSize ? parseInt(store.flatSize) : -1,
        flat_object_names: store.images
          .filter((img) => img.object_name)
          .map((img) => img.object_name),
        subsidised: store.isSubsidised,
        rent: parseInt(store.rent),
        utilities: parseInt(store.utilities),
        equipment_fees:
          store.furnishing === "unfurnished"
            ? 0
            : parseInt(store.equipmentFees || "0"),
        deposit: parseInt(store.deposit),
        room_size: parseInt(store.roomSize),
        permanent: store.isPermanent,
        from_date: fromDateIso,
        to_date: toDateIso,
        languages: [],
        furnishing: store.furnishing,
        flat_question: store.question,
        attributes: store.flatAttributes.map((attr) => parseInt(attr)),
      });

      setCreatedFlat({
        flat_id: result.flat_id,
        room_id: result.room_id,
        join_code: result.join_code,
      });
      posthog?.capture("flat_creation_success", {
        flat_id: result.flat_id,
        room_id: result.room_id,
        has_flat_video: false,
        image_count: store.images.filter((img) => img.object_name).length,
        is_permanent: store.isPermanent,
        is_subsidised: store.isSubsidised,
        attribute_count: store.flatAttributes.length,
        member_count: Math.max(2, parseInt(store.memberCount) || 0),
      });
      posthog?.capture("create_flat_submit_success", {
        surface: "create_flat_page",
        step: store.step,
        is_authenticated: !!user,
      });
      store.nextStep();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create flat. Please try again.";
      console.error("Error creating flat:", error);
      posthog?.capture("flat_creation_error", {
        error: message,
      });
      posthog?.capture("create_flat_submit_failed", {
        surface: "create_flat_page",
        step: store.step,
        is_authenticated: !!user,
        code: "create_flat_failed",
        message,
      });
      setSubmitError(
        message ||
          t("create_flat_error") ||
          "Failed to create flat. Please try again.",
      );
    } finally {
      clearTurnstileToken();
      resetToken();
      setIsSubmitting(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    handleSubmit();
  };

  const handleAuthRequired = () => {
    setShowAuthModal(true);
  };

  const handleViewFlat = () => {
    if (createdFlat) {
      store.reset();
      navigate(`/app/flat-view/${createdFlat.flat_id}`, { replace: true });
    }
  };

  const renderStep = () => {
    switch (store.step) {
      case 0:
        return <FlatNameStep store={store} />;
      case 1:
        return <MembersStep store={store} />;
      case 2:
        return (
          <LocationStep
            store={store}
            onLocationSelected={() => {
              if (store.step === 2) {
                store.nextStep();
              }
            }}
          />
        );
      case 3:
        return <AvailabilityStep store={store} />;
      case 4:
        return <PricingStep store={store} />;
      case 5:
        return <RoomDetailsStep store={store} />;
      case 6:
        return <ImagesStep store={store} onAuthRequired={handleAuthRequired} />;
      case 7:
        return <DescriptionStep store={store} />;
      case 8:
        return <AttributesStep store={store} />;
      case 9:
        return <QuestionsStep store={store} />;
      case 10:
        return createdFlat ? (
          <SuccessStep createdFlat={createdFlat} onViewFlat={handleViewFlat} />
        ) : null;
      default:
        return null;
    }
  };

  const isCurrentStepValid = store.isStepValid(store.step);
  const isLastStep = store.step === NUMBER_OF_STEPS - 1;
  const isSuccessStep = store.step === NUMBER_OF_STEPS;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        isCurrentStepValid &&
        !isSubmitting &&
        !isSuccessStep
      ) {
        const target = e.target as HTMLElement;
        if (target.tagName === "TEXTAREA") return;

        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isCurrentStepValid, isSubmitting, isSuccessStep]);

  if (user && (profileLoading || !isProfileComplete)) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: containerNeutralSolidSurface }}
      >
        <p style={{ color: textNeutralDefault }}>
          {t("loading") || "Loading..."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="h-screen flex flex-col font-gnarly-regular relative overflow-hidden"
      style={{
        backgroundColor: containerNeutralSolidSurface,
        color: textNeutralDefault,
      }}
    >
      {/* Subtle ambient gradient */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 50% at 50% 20%, rgba(91, 61, 246, 0.035) 0%, transparent 70%)`,
        }}
      />

      {/* Header */}
      <header
        className="flex-shrink-0 z-40"
        style={{ backgroundColor: containerNeutralSolidSurface }}
      >
        <div className="px-5 sm:px-8 pt-5 pb-4 flex items-center justify-between">
          {/* Step counter */}
          {!isSuccessStep ? (
            <span
              className="text-xs font-medium tracking-widest uppercase"
              style={{ color: textNeutralMuted }}
            >
              {store.step + 1} / {NUMBER_OF_STEPS}
            </span>
          ) : (
            <div />
          )}

          {/* Close button */}
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105"
            style={{
              backgroundColor: "rgba(2, 2, 16, 0.05)",
              color: textNeutralMuted,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Segmented progress bar */}
        {!isSuccessStep && (
          <div className="flex gap-1.5 px-5 sm:px-8 pb-4">
            {Array.from({ length: NUMBER_OF_STEPS }, (_, i) => (
              <div
                key={i}
                className="h-1 flex-1 rounded-full transition-all duration-500 ease-out"
                style={{
                  backgroundColor:
                    i <= store.step ? accentColor : containerNeutralLightMedium,
                  opacity: i < store.step ? 0.55 : i === store.step ? 1 : 0.35,
                }}
              />
            ))}
          </div>
        )}
      </header>

      {/* Content */}
      <main className={`flex-1 min-h-0 px-5 sm:px-8 pb-8 flex justify-center relative z-10 overflow-y-auto overflow-x-hidden ${store.step === 2 ? "items-start pt-[10vh]" : "items-start pt-8"}`}>
        <div key={store.step} className="w-full max-w-md min-w-0 my-auto">
          {renderStep()}

          {submitError && (
            <div
              className="mt-6 px-5 py-4 rounded-2xl text-sm font-medium"
              style={{
                backgroundColor: "rgba(220, 54, 46, 0.08)",
                color: "#B02B25",
                border: "1px solid rgba(220, 54, 46, 0.15)",
              }}
            >
              {submitError}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      {!isSuccessStep && (
        <footer
          className="flex-shrink-0 px-5 sm:px-8 py-5 relative z-10"
          style={{
            backgroundColor: containerNeutralSolidSurface,
          }}
        >
          <div className="max-w-md mx-auto flex items-center justify-between gap-4">
            {store.step > 0 ? (
              <button
                onClick={handleBack}
                className="group flex items-center gap-2 px-6 py-3.5 rounded-full font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.06)",
                  color: textNeutralDefault,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform group-hover:-translate-x-0.5"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                {t("back") || "Back"}
              </button>
            ) : (
              <div />
            )}

            <button
              onClick={handleNext}
              disabled={!isCurrentStepValid || isSubmitting}
              className="group flex items-center gap-2 px-8 py-3.5 rounded-full font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                backgroundColor: accentColor,
                color: textNeutralOnPrimary,
              }}
            >
              {isSubmitting
                ? t("creating") || "Creating..."
                : isLastStep
                  ? t("create_flat") || "Create Flat"
                  : t("next") || "Next"}
              {!isSubmitting && !isLastStep && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform group-hover:translate-x-0.5"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              )}
            </button>
          </div>
        </footer>
      )}

      {/* Verify Email Modal */}
      {showVerifyEmailModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-md w-full p-6"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              borderRadius: 20,
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: `${accentColor}14` }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={accentColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>

            <h2
              className="text-xl font-gnarly-semibold mb-2 text-center"
              style={{ color: textNeutralDefault }}
            >
              {t("verify_email_title") || "Verify your email"}
            </h2>
            <p
              className="text-sm leading-relaxed text-center mb-6"
              style={{ color: textNeutralMuted }}
            >
              {t("verify_email_desc_create") ||
                "You need to verify your email address before you can create a flat. Check your inbox for a verification link."}
            </p>

            {verifyEmailSent && !verifyEmailError && (
              <div
                className="px-4 py-3 rounded-xl text-sm text-center mb-4"
                style={{
                  backgroundColor: "rgba(34, 197, 94, 0.08)",
                  color: "#16a34a",
                }}
              >
                {t("verify_email_sent") ||
                  "Verification email sent! Check your inbox."}
              </div>
            )}

            {verifyEmailError && (
              <div
                className="px-4 py-3 rounded-xl text-sm text-center mb-4"
                style={{
                  backgroundColor: "rgba(220, 54, 46, 0.07)",
                  color: "#B02B25",
                }}
              >
                {verifyEmailError}
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              <button
                onClick={handleRefreshVerification}
                className="w-full py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  backgroundColor: accentColor,
                  color: textNeutralOnPrimary,
                }}
              >
                {t("verify_email_done") || "I've verified my email"}
              </button>
              <button
                onClick={handleResendVerification}
                className="w-full py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.05)",
                  color: textNeutralDefault,
                }}
              >
                {t("verify_email_resend") || "Resend verification email"}
              </button>
              <button
                onClick={() => setShowVerifyEmailModal(false)}
                className="w-full py-3 text-sm transition-opacity hover:opacity-70"
                style={{ color: textNeutralMuted }}
              >
                {t("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Turnstile verification modal */}
      {showTurnstileModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-sm w-full p-6"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              borderRadius: 20,
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: `${accentColor}14` }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={accentColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>

            <h2
              className="text-xl font-gnarly-semibold mb-2 text-center"
              style={{ color: textNeutralDefault }}
            >
              {t("verify_human_title") || "Quick verification"}
            </h2>
            <p
              className="text-sm leading-relaxed text-center mb-6"
              style={{ color: textNeutralMuted }}
            >
              {t("verify_human_desc") ||
                "Please complete the check below to create your flat."}
            </p>

            {turnstileError ? (
              <div className="flex flex-col items-center gap-3 mb-4">
                <p
                  className="text-sm text-center"
                  style={{ color: "#B02B25" }}
                >
                  {t("verify_human_error") ||
                    "Verification failed. Please try again."}
                </p>
                <button
                  onClick={() => {
                    posthog?.capture("turnstile_failed", {
                      surface: "create_flat_page",
                      step: store.step,
                      is_authenticated: !!user,
                    });
                    resetToken();
                  }}
                  className="px-6 py-2.5 rounded-full text-sm font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: accentColor,
                    color: textNeutralOnPrimary,
                  }}
                >
                  {t("try_again") || "Try again"}
                </button>
              </div>
            ) : (
              <div className="flex justify-center mb-4">
                <TurnstileWidget
                  siteKey={siteKey}
                  widgetRef={widgetRef}
                  onSuccess={handleTurnstileSuccess}
                  onExpire={onExpire}
                  onError={() => {
                    posthog?.capture("turnstile_failed", {
                      surface: "create_flat_page",
                      step: store.step,
                      is_authenticated: !!user,
                    });
                    onError();
                  }}
                  appearance="always"
                />
              </div>
            )}

            <button
              onClick={() => {
                posthog?.capture("turnstile_cancelled", {
                  surface: "create_flat_page",
                  step: store.step,
                  is_authenticated: !!user,
                });
                setShowTurnstileModal(false);
                resetToken();
              }}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ color: textNeutralMuted }}
            >
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default CreateFlat;
